import React, { useState, useEffect } from 'react';
import masterService from '../services/masterService';
import { formatDataKeyCuiaba } from '../utils/dateUtils';
import multiLojasService from '../services/multiLojasService';
import { BarChart3, Home, Scissors, Settings, Eye, Edit2, Trash2, Users, TrendingUp, DollarSign, CreditCard, Clock, Store, Building2, Copy, ExternalLink, QrCode } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Paleta de cores
const COLORS = {
  primary: '#FFFFFF',    // Branco
  secondary: '#000000',  // Preto
  tertiary: '#F5F5F5',   // Cinza claro
  quaternary: '#3F45BA', // Azul
  quinary: '#EF4910'     // Laranja/Vermelho
};

const STATUS_OPTIONS = [
  { value: 'trial', label: 'Em teste' },
  { value: 'pending_payment', label: 'Pagamento pendente' },
  { value: 'past_due', label: 'Trial vencido (past_due)' },
  { value: 'suspended', label: 'Suspensa' },
  { value: 'active', label: 'Assinatura ativa' },
  { value: 'complimentary', label: 'Cortesia / Acesso ilimitado' }
];

function MasterDashboard({ user, onLogout }) {
  const [currentPage, setCurrentPage] = useState('dashboard'); // dashboard, barbearias, configuracoes, multi-lojas
  const [barbearias, setBarbearias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxFiliaisConfigs, setMaxFiliaisConfigs] = useState({}); // { barbeariaId: maxFiliais }
  const [savingConfig, setSavingConfig] = useState(null);
  const [filiaisPorMatriz, setFiliaisPorMatriz] = useState({}); // { matrizId: [filiais] }
  const [dashboardStats, setDashboardStats] = useState({
    barbeariasAtivas: 0,
    barbeariasTrial: 0,
    faturamentoMensal: 0,
    comparativoMensal: []
  });

  // Estados para formulários e modais
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    telefone: '',
    endereco: '',
    trialUnlimited: false
  });
  const [showAdminForm, setShowAdminForm] = useState(null);
  const [adminData, setAdminData] = useState({
    nome: '',
    email: '',
    senha: '',
    telefone: ''
  });
  const [showEfiConfig, setShowEfiConfig] = useState(null);
  const [efiConfig, setEfiConfig] = useState({
    clientId: '',
    clientSecret: '',
    pixKey: '',
    environment: 'sandbox',
    certificatePassword: ''
  });
  const [efiConfigs, setEfiConfigs] = useState({});
  const [masterEfiConfig, setMasterEfiConfig] = useState({
    clientId: '',
    clientSecret: '',
    pixKey: '',
    environment: 'sandbox',
    certificatePassword: '',
    certificateFilename: ''
  });
  const [masterConfigLoading, setMasterConfigLoading] = useState(true);
  const [masterConfigSaving, setMasterConfigSaving] = useState(false);
  const [masterHasCertificate, setMasterHasCertificate] = useState(false);
  const [excluindoId, setExcluindoId] = useState(null);
  const [editingTrial, setEditingTrial] = useState(null);
  const [trialForm, setTrialForm] = useState({
    trialUnlimited: false,
    trialEndsAt: '',
    subscriptionStatus: 'trial'
  });
  const [savingTrial, setSavingTrial] = useState(false);
  const [showBarbeariaDetails, setShowBarbeariaDetails] = useState(null);
  const [barbeariaUsers, setBarbeariaUsers] = useState({});

  // Carregar estatísticas do dashboard
  const carregarDashboardStats = async () => {
    try {
      const stats = await masterService.obterDashboardEstatisticas();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas do dashboard:', error);
    }
  };

  const carregarMasterEfiConfig = async () => {
    try {
      setMasterConfigLoading(true);
      const config = await masterService.buscarEfiMasterConfig();
      setMasterEfiConfig({
        clientId: config.clientId || '',
        clientSecret: '',
        pixKey: config.pixKey || '',
        environment: config.environment || 'sandbox',
        certificatePassword: config.certificatePassword || '',
        certificateFilename: config.certificateFilename || ''
      });
      setMasterHasCertificate(!!config.hasCertificate);
    } catch (error) {
      console.error('Erro ao carregar credenciais master EFI:', error);
    } finally {
      setMasterConfigLoading(false);
    }
  };

  const carregarMaxFiliaisConfigs = async () => {
    try {
      const configs = {};
      for (const barbearia of barbearias) {
        if (!barbearia.parentId) { // Apenas matrizes
          try {
            const config = await multiLojasService.getConfig(barbearia.id);
            configs[barbearia.id] = config.maxFiliais || 1;
          } catch (err) {
            configs[barbearia.id] = 1; // Default
          }
        }
      }
      setMaxFiliaisConfigs(configs);
    } catch (error) {
      console.error('Erro ao carregar configs de maxFiliais:', error);
    }
  };

  const salvarMaxFiliais = async (barbeariaId, maxFiliais) => {
    try {
      setSavingConfig(barbeariaId);
      await multiLojasService.setConfig(barbeariaId, { maxFiliais: parseInt(maxFiliais) || 1 });
      setMaxFiliaisConfigs(prev => ({ ...prev, [barbeariaId]: parseInt(maxFiliais) || 1 }));
      alert('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar maxFiliais:', error);
      alert(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setSavingConfig(null);
    }
  };

  useEffect(() => {
    if (currentPage === 'dashboard') {
      carregarDashboardStats();
    }
    if (currentPage === 'barbearias') {
      carregarBarbearias();
    }
    if (currentPage === 'configuracoes') {
      carregarMasterEfiConfig();
    }
    if (currentPage === 'multi-lojas') {
      if (barbearias.length > 0) {
        carregarMaxFiliaisConfigs();
      } else {
        carregarBarbearias().then(() => {
          setTimeout(() => carregarMaxFiliaisConfigs(), 500);
        });
      }
    }
  }, [currentPage]);

  // Carregar configurações Efí ao carregar barbearias
  const carregarEfiConfig = async (barbeariaId) => {
    try {
      const config = await masterService.buscarEfiConfig(barbeariaId);
      setEfiConfigs(prev => ({ ...prev, [barbeariaId]: config }));
      if (showEfiConfig === barbeariaId) {
        setEfiConfig({
          clientId: config.clientId || '',
          clientSecret: '',
          pixKey: config.pixKey || '',
          environment: config.environment || 'sandbox',
          certificatePassword: config.certificatePassword || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar config Efí:', error);
    }
  };

  const carregarFiliaisMatriz = async (matrizId) => {
    try {
      const filiais = await multiLojasService.listFiliais(matrizId);
      const data = Array.isArray(filiais) ? filiais : (filiais.data || []);
      setFiliaisPorMatriz(prev => ({ ...prev, [matrizId]: data }));
    } catch (error) {
      console.error('Erro ao carregar filiais:', error);
      setFiliaisPorMatriz(prev => ({ ...prev, [matrizId]: [] }));
    }
  };

  const carregarBarbearias = async () => {
    try {
      setLoading(true);
      const data = await masterService.listarBarbearias();
      const barbeariasList = Array.isArray(data) ? data : [];
      // Filtrar apenas matrizes (sem parentId)
      const matrizes = barbeariasList.filter(b => !b.parentId);
      setBarbearias(matrizes);
      
      // Carregar configurações Efí para cada barbearia
      for (const barbearia of matrizes) {
        await carregarEfiConfig(barbearia.id);
        await carregarUsuariosBarbearia(barbearia.id);
      }
    } catch (error) {
      console.error('Erro ao carregar barbearias:', error);
      alert('Erro ao carregar barbearias');
      setBarbearias([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarUsuariosBarbearia = async (barbeariaId) => {
    try {
      const barbearia = await masterService.buscarBarbearia(barbeariaId);
      setBarbeariaUsers(prev => ({
        ...prev,
        [barbeariaId]: barbearia.usuarios || []
      }));
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleSalvarEfiConfig = async (barbeariaId) => {
    try {
      await masterService.salvarEfiConfig(barbeariaId, efiConfig);
      alert('Configurações Efí salvas com sucesso!');
      await carregarEfiConfig(barbeariaId);
      setShowEfiConfig(null);
      setEfiConfig({ clientId: '', clientSecret: '', pixKey: '', environment: 'sandbox', certificatePassword: '' });
    } catch (error) {
      console.error('Erro ao salvar config Efí:', error);
      alert(error.response?.data?.message || 'Erro ao salvar configurações Efí');
    }
  };

  const handleUploadCertificate = async (barbeariaId, file) => {
    try {
      await masterService.uploadEfiCertificate(barbeariaId, file);
      alert('Certificado enviado com sucesso!');
      await carregarEfiConfig(barbeariaId);
    } catch (error) {
      console.error('Erro ao fazer upload do certificado:', error);
      alert(error.response?.data?.message || 'Erro ao fazer upload do certificado');
    }
  };

  const handleUploadKioskVideo = async (barbeariaId, file) => {
    try {
      await masterService.uploadKioskVideo(barbeariaId, file);
      await carregarBarbearias();
    } catch (error) {
      console.error('Erro ao fazer upload do vídeo do kiosk:', error);
      throw error;
    }
  };

  const handleDeleteKioskVideo = async (barbeariaId) => {
    try {
      await masterService.deleteKioskVideo(barbeariaId);
      await carregarBarbearias();
    } catch (error) {
      console.error('Erro ao remover vídeo do kiosk:', error);
      throw error;
    }
  };

  const handleUploadMasterCertificate = async (file) => {
    if (!file) return;
    try {
      await masterService.uploadEfiMasterCertificate(file);
      alert('Certificado master enviado com sucesso!');
      await carregarMasterEfiConfig();
    } catch (error) {
      console.error('Erro ao enviar certificado master:', error);
      alert(error.response?.data?.message || 'Erro ao enviar certificado master');
    }
  };

  const handleSalvarMasterEfiConfig = async () => {
    try {
      setMasterConfigSaving(true);
      await masterService.salvarEfiMasterConfig({
        clientId: masterEfiConfig.clientId,
        clientSecret: masterEfiConfig.clientSecret,
        pixKey: masterEfiConfig.pixKey,
        environment: masterEfiConfig.environment,
        certificatePassword: masterEfiConfig.certificatePassword
      });
      alert('Credenciais master salvas com sucesso!');
      await carregarMasterEfiConfig();
    } catch (error) {
      console.error('Erro ao salvar credenciais master:', error);
      alert(error.response?.data?.message || 'Erro ao salvar credenciais master');
    } finally {
      setMasterConfigSaving(false);
    }
  };

  const handleSubmitBarbearia = async (e) => {
    e.preventDefault();
    try {
      await masterService.criarBarbearia(formData);
      alert('Barbearia criada com sucesso!');
      setShowForm(false);
      setFormData({ nome: '', cnpj: '', telefone: '', endereco: '', trialUnlimited: false });
      carregarBarbearias();
      carregarDashboardStats();
    } catch (error) {
      console.error('Erro ao criar barbearia:', error);
      alert(error.response?.data?.message || 'Erro ao criar barbearia');
    }
  };

  const handleToggleBarbearia = async (id) => {
    try {
      await masterService.toggleBarbearia(id);
      alert('Status alterado com sucesso!');
      carregarBarbearias();
      carregarDashboardStats();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status da barbearia');
    }
  };

  const handleSubmitAdmin = async (e, barbeariaId) => {
    e.preventDefault();
    try {
      await masterService.criarAdmin(barbeariaId, adminData);
      alert('Administrador criado com sucesso!');
      setShowAdminForm(null);
      setAdminData({ nome: '', email: '', senha: '', telefone: '' });
      carregarUsuariosBarbearia(barbeariaId);
    } catch (error) {
      console.error('Erro ao criar admin:', error);
      alert(error.response?.data?.message || 'Erro ao criar administrador');
    }
  };

  const handleExcluirBarbearia = async (barbearia) => {
    const confirmacao = window.confirm(
      `Tem certeza que deseja excluir a barbearia "${barbearia.nome}"?\n\n` +
      'Esta ação é permanente e removerá todos os dados associados (usuários, barbeiros, serviços, comandas, saques, configurações e arquivos).'
    );

    if (!confirmacao) return;

    try {
      setExcluindoId(barbearia.id);
      await masterService.excluirBarbearia(barbearia.id);
      alert('Barbearia excluída com sucesso!');
      await carregarBarbearias();
      carregarDashboardStats();
    } catch (error) {
      console.error('Erro ao excluir barbearia:', error);
      alert(error.response?.data?.message || 'Erro ao excluir barbearia');
    } finally {
      setExcluindoId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR');
  };

  const calcularDiasRestantes = (barbearia) => {
    if (barbearia.isTrialUnlimited) return 'Ilimitado';
    if (!barbearia.trialEndsAt) return null;
    const diff = new Date(barbearia.trialEndsAt).getTime() - Date.now();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getSubscriptionBadge = (barbearia) => {
    const status = (barbearia.subscriptionStatus || '').toLowerCase();
    const map = {
      trial: { label: 'Em teste', style: { backgroundColor: `${COLORS.quaternary}20`, color: COLORS.quaternary } },
      pending_payment: { label: 'Pagamento pendente', style: { backgroundColor: `${COLORS.quinary}20`, color: COLORS.quinary } },
      past_due: { label: 'Em atraso', style: { backgroundColor: `${COLORS.quinary}20`, color: COLORS.quinary } },
      suspended: { label: 'Suspensa', style: { backgroundColor: `${COLORS.quinary}20`, color: COLORS.quinary } },
      active: { label: 'Assinatura ativa', style: { backgroundColor: `${COLORS.quaternary}20`, color: COLORS.quaternary } },
      complimentary: { label: 'Acesso ilimitado', style: { backgroundColor: `${COLORS.quaternary}20`, color: COLORS.quaternary } }
    };
    return map[status] || { label: 'Sem status', style: { backgroundColor: COLORS.tertiary, color: COLORS.secondary, opacity: 0.7 } };
  };

  const openTrialModal = (barbearia) => {
    setEditingTrial(barbearia);
    setTrialForm({
      trialUnlimited: Boolean(barbearia.isTrialUnlimited),
      trialEndsAt: barbearia.trialEndsAt
        ? formatDataKeyCuiaba(barbearia.trialEndsAt)
        : '',
      subscriptionStatus: barbearia.subscriptionStatus || 'trial'
    });
  };

  const handleSalvarTrial = async () => {
    if (!editingTrial) return;
    try {
      setSavingTrial(true);

      if (
        trialForm.trialEndsAt &&
        Number.isNaN(new Date(trialForm.trialEndsAt).getTime())
      ) {
        alert('Data de término inválida.');
        setSavingTrial(false);
        return;
      }

      const payload = {
        trialUnlimited: Boolean(trialForm.trialUnlimited),
        subscriptionStatus: trialForm.subscriptionStatus || null
      };

      if (payload.trialUnlimited) {
        payload.trialEndsAt = null;
      } else {
        payload.trialEndsAt = trialForm.trialEndsAt
          ? new Date(`${trialForm.trialEndsAt}T00:00:00-04:00`).toISOString()
          : null;
      }

      await masterService.editarBarbearia(editingTrial.id, payload);
      alert('Dados de trial/assinatura atualizados!');
      setEditingTrial(null);
      await carregarBarbearias();
      carregarDashboardStats();
    } catch (error) {
      console.error('Erro ao atualizar trial:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar informações de trial.');
    } finally {
      setSavingTrial(false);
    }
  };

  // Renderizar Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ color: COLORS.secondary }}>Dashboard</h2>
        <button
          onClick={carregarDashboardStats}
          className="px-4 py-2 text-white rounded transition-colors"
          style={{ backgroundColor: COLORS.quaternary }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
          onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
        >
          Atualizar
        </button>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>Barbearias Ativas</p>
              <p className="text-3xl font-bold mt-2" style={{ color: COLORS.quaternary }}>{dashboardStats.barbeariasAtivas}</p>
              <p className="text-xs mt-1" style={{ color: COLORS.secondary, opacity: 0.5 }}>Pagantes</p>
            </div>
            <div style={{ backgroundColor: `${COLORS.quaternary}20` }} className="p-3 rounded-full">
              <Scissors className="w-8 h-8" style={{ color: COLORS.quaternary }} />
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>Barbearias em Trial</p>
              <p className="text-3xl font-bold mt-2" style={{ color: COLORS.quaternary }}>{dashboardStats.barbeariasTrial}</p>
              <p className="text-xs mt-1" style={{ color: COLORS.secondary, opacity: 0.5 }}>Em teste</p>
            </div>
            <div style={{ backgroundColor: `${COLORS.quaternary}20` }} className="p-3 rounded-full">
              <Clock className="w-8 h-8" style={{ color: COLORS.quaternary }} />
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>Faturamento Mensal</p>
              <p className="text-3xl font-bold mt-2" style={{ color: COLORS.quinary }}>
                R$ {dashboardStats.faturamentoMensal.toFixed(2).replace('.', ',')}
              </p>
              <p className="text-xs mt-1" style={{ color: COLORS.secondary, opacity: 0.5 }}>Este mês</p>
            </div>
            <div style={{ backgroundColor: `${COLORS.quinary}20` }} className="p-3 rounded-full">
              <DollarSign className="w-8 h-8" style={{ color: COLORS.quinary }} />
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>Total de Barbearias</p>
              <p className="text-3xl font-bold mt-2" style={{ color: COLORS.secondary }}>
                {dashboardStats.barbeariasAtivas + dashboardStats.barbeariasTrial}
              </p>
              <p className="text-xs mt-1" style={{ color: COLORS.secondary, opacity: 0.5 }}>Cadastradas</p>
            </div>
            <div style={{ backgroundColor: COLORS.tertiary }} className="p-3 rounded-full">
              <BarChart3 className="w-8 h-8" style={{ color: COLORS.secondary, opacity: 0.7 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Comparativo */}
      {dashboardStats.comparativoMensal.length > 0 && (
        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.secondary }}>Comparativo de Faturamento - Últimos 6 Meses</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dashboardStats.comparativoMensal}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.tertiary} />
              <XAxis dataKey="mes" stroke={COLORS.secondary} style={{ opacity: 0.7 }} />
              <YAxis stroke={COLORS.secondary} style={{ opacity: 0.7 }} />
              <Tooltip 
                formatter={(value) => `R$ ${value.toFixed(2).replace('.', ',')}`}
                labelStyle={{ color: COLORS.secondary }}
                contentStyle={{ backgroundColor: COLORS.primary, border: `1px solid ${COLORS.tertiary}` }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="faturamento" 
                stroke={COLORS.quaternary} 
                strokeWidth={2}
                name="Faturamento"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );

  // Renderizar página de Barbearias
  const renderBarbearias = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ color: COLORS.secondary }}>Barbearias</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 text-white rounded transition-colors"
          style={{ backgroundColor: COLORS.quaternary }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
          onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
        >
          {showForm ? 'Cancelar' : '+ Nova Barbearia'}
        </button>
      </div>

      {/* Formulário Nova Barbearia */}
      {showForm && (
        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.secondary }}>Cadastrar Nova Barbearia</h3>
          <form onSubmit={handleSubmitBarbearia}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CNPJ</label>
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Telefone</label>
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Endereço</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium mb-1">
                  <input
                    type="checkbox"
                    checked={formData.trialUnlimited}
                    onChange={(e) => setFormData({ ...formData, trialUnlimited: e.target.checked })}
                    className="w-4 h-4"
                  />
                  Acesso ilimitado (sem cobrança)
                </label>
              </div>
            </div>
            <div className="mt-4">
                <button
                  type="submit"
                  className="text-white px-6 py-2 rounded transition-colors"
                  style={{ backgroundColor: COLORS.quaternary }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                >
                  Criar Barbearia
                </button>
              </div>
          </form>
        </div>
      )}

      {/* Cards de Barbearias */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-xl">Carregando...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {barbearias.map((barbearia) => {
            const diasRestantes = calcularDiasRestantes(barbearia);
            const subscriptionBadge = getSubscriptionBadge(barbearia);
            const usuarios = barbeariaUsers[barbearia.id] || barbearia.usuarios || [];
            
            return (
              <div key={barbearia.id} style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold" style={{ color: COLORS.secondary }}>{barbearia.nome}</h3>
                    <p className="text-xs mt-1" style={{ color: COLORS.secondary, opacity: 0.5 }}>ID: {barbearia.id}</p>
                  </div>
                  <span 
                    className="px-2 py-1 rounded text-xs"
                    style={{ 
                      backgroundColor: barbearia.ativo ? `${COLORS.quaternary}20` : `${COLORS.quinary}20`,
                      color: barbearia.ativo ? COLORS.quaternary : COLORS.quinary
                    }}
                  >
                    {barbearia.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm mb-4" style={{ color: COLORS.secondary, opacity: 0.8 }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 rounded-full font-semibold text-xs" style={subscriptionBadge.style}>
                      {subscriptionBadge.label}
                    </span>
                    {diasRestantes !== null && (
                      <span 
                        className="px-2 py-1 rounded-full font-semibold text-xs"
                        style={{ backgroundColor: `${COLORS.quaternary}20`, color: COLORS.quaternary }}
                      >
                        {diasRestantes === 'Ilimitado' ? 'Trial ilimitado' : `${diasRestantes} dia(s)`}
                      </span>
                    )}
                  </div>
                  {barbearia.cnpj && <p><strong>CNPJ:</strong> {barbearia.cnpj}</p>}
                  {barbearia.telefone && <p><strong>Telefone:</strong> {barbearia.telefone}</p>}
                  <p><strong>Usuários:</strong> {usuarios.length}</p>
                </div>

                {/* Botões de Ação */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowBarbeariaDetails(showBarbeariaDetails === barbearia.id ? null : barbearia.id)}
                    className="flex-1 text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1 transition-colors"
                    style={{ backgroundColor: COLORS.quaternary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                    title="Ver detalhes e configurações"
                  >
                    <Eye className="w-4 h-4" />
                    Detalhes
                  </button>
                  <button
                    onClick={() => openTrialModal(barbearia)}
                    className="text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1 transition-colors"
                    style={{ backgroundColor: COLORS.quaternary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                    title="Editar plano"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleExcluirBarbearia(barbearia)}
                    disabled={excluindoId === barbearia.id}
                    className="text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ backgroundColor: COLORS.quinary }}
                    onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#d13e0e')}
                    onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = COLORS.quinary)}
                    title="Excluir barbearia"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {barbearias.length === 0 && !loading && (
        <div className="text-center py-12 rounded-lg shadow" style={{ backgroundColor: COLORS.primary }}>
          <p style={{ color: COLORS.secondary, opacity: 0.7 }}>Nenhuma barbearia cadastrada ainda.</p>
          <p className="text-sm mt-2" style={{ color: COLORS.secondary, opacity: 0.5 }}>Clique em "Nova Barbearia" para começar.</p>
        </div>
      )}
    </div>
  );

  // Renderizar página de Configurações
  const renderConfiguracoes = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold" style={{ color: COLORS.secondary }}>Configurações</h2>

      <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-xl font-semibold" style={{ color: COLORS.secondary }}>Credenciais EFI Master</h3>
            <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>
              Configure as chaves e o certificado utilizados para cobrar as barbearias via EFI.
            </p>
          </div>
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: masterHasCertificate ? `${COLORS.quaternary}20` : `${COLORS.quinary}20`,
              color: masterHasCertificate ? COLORS.quaternary : COLORS.quinary
            }}
          >
            {masterHasCertificate ? 'Certificado disponível' : 'Certificado pendente'}
          </span>
        </div>

        {masterConfigLoading ? (
          <div className="animate-pulse text-sm text-slate-500">Carregando credenciais...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="md:col-span-2 border border-dashed border-slate-200 rounded-lg p-4 bg-slate-50">
              <label className="block text-xs font-semibold text-slate-700 mb-2">
                Certificado .p12 (obrigatório)
              </label>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <input
                  type="file"
                  accept=".p12,application/x-pkcs12"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    handleUploadMasterCertificate(file);
                    e.target.value = '';
                  }}
                  className="text-xs"
                />
                {masterEfiConfig.certificateFilename && (
                  <span className="text-xs text-slate-500">
                    Arquivo atual: {masterEfiConfig.certificateFilename}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Client ID *</label>
              <input
                type="text"
                value={masterEfiConfig.clientId}
                onChange={(e) => setMasterEfiConfig({ ...masterEfiConfig, clientId: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="client_id_123"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Client Secret *</label>
              <input
                type="password"
                value={masterEfiConfig.clientSecret}
                onChange={(e) => setMasterEfiConfig({ ...masterEfiConfig, clientSecret: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="Informe o secret completo"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Chave Pix *</label>
              <input
                type="text"
                value={masterEfiConfig.pixKey}
                onChange={(e) => setMasterEfiConfig({ ...masterEfiConfig, pixKey: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="Chave Pix da EFI"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Senha do certificado</label>
              <input
                type="password"
                value={masterEfiConfig.certificatePassword}
                onChange={(e) =>
                  setMasterEfiConfig({ ...masterEfiConfig, certificatePassword: e.target.value })
                }
                className="w-full border rounded px-3 py-2"
                placeholder="Deixe vazio se não possuir senha"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Ambiente *</label>
              <select
                value={masterEfiConfig.environment}
                onChange={(e) =>
                  setMasterEfiConfig({ ...masterEfiConfig, environment: e.target.value })
                }
                className="w-full border rounded px-3 py-2"
              >
                <option value="sandbox">Homologação (Sandbox)</option>
                <option value="production">Produção</option>
              </select>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <button
                onClick={handleSalvarMasterEfiConfig}
                disabled={masterConfigSaving}
                className="px-6 py-2 rounded text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: COLORS.quaternary }}
                onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#2d33a0')}
                onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = COLORS.quaternary)}
              >
                {masterConfigSaving ? 'Salvando...' : 'Salvar credenciais master'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Renderizar página Multi-Lojas
  const renderMultiLojas = () => {
    const matrizes = barbearias.filter(b => !b.parentId);
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold" style={{ color: COLORS.secondary }}>Configurações Multi-Lojas</h2>
        </div>

        <div style={{ backgroundColor: COLORS.primary }} className="rounded-lg shadow p-6">
          <p className="text-sm mb-6" style={{ color: COLORS.secondary, opacity: 0.7 }}>
            Configure o limite máximo de filiais que cada barbearia pode criar.
          </p>

          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : matrizes.length === 0 ? (
            <div className="text-center py-8" style={{ color: COLORS.secondary, opacity: 0.7 }}>
              Nenhuma barbearia cadastrada ainda.
            </div>
          ) : (
            <div className="space-y-4">
              {matrizes.map((barbearia) => (
                <div
                  key={barbearia.id}
                  className="border rounded-lg p-4"
                  style={{ borderColor: COLORS.tertiary }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold" style={{ color: COLORS.secondary }}>
                        {barbearia.nome}
                      </h3>
                      <p className="text-xs" style={{ color: COLORS.secondary, opacity: 0.6 }}>
                        ID: {barbearia.id}
                      </p>
                    </div>
                    <span
                      className="px-2 py-1 rounded text-xs"
                      style={{
                        backgroundColor: barbearia.ativo ? `${COLORS.quaternary}20` : `${COLORS.quinary}20`,
                        color: barbearia.ativo ? COLORS.quaternary : COLORS.quinary
                      }}
                    >
                      {barbearia.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1" style={{ color: COLORS.secondary }}>
                        Máximo de Filiais Permitidas
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={maxFiliaisConfigs[barbearia.id] || 1}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          setMaxFiliaisConfigs(prev => ({ ...prev, [barbearia.id]: val }));
                        }}
                        className="w-full border rounded px-3 py-2"
                        style={{ borderColor: COLORS.tertiary }}
                      />
                      <p className="text-xs mt-1" style={{ color: COLORS.secondary, opacity: 0.6 }}>
                        Por padrão, cada barbearia começa com 1 loja (a própria matriz).
                      </p>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => salvarMaxFiliais(barbearia.id, maxFiliaisConfigs[barbearia.id] || 1)}
                        disabled={savingConfig === barbearia.id}
                        className="px-4 py-2 rounded text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        style={{ backgroundColor: COLORS.quaternary }}
                        onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#2d33a0')}
                        onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = COLORS.quaternary)}
                      >
                        {savingConfig === barbearia.id ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.tertiary }}>
      {/* Header */}
      <header style={{ backgroundColor: COLORS.primary, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img 
              src="/Barbxpro.png" 
              alt="Barbx Logo" 
              className="h-12 w-auto"
              style={{ maxHeight: '48px' }}
            />
            <div>
              <h1 className="text-2xl font-bold" style={{ color: COLORS.secondary }}>Painel Master - Barbx</h1>
              <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>Olá, {user.nome}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="px-4 py-2 rounded text-white transition-colors"
            style={{ backgroundColor: COLORS.quinary }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#d13e0e'}
            onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quinary}
          >
            Sair
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav style={{ backgroundColor: COLORS.primary, borderBottom: `1px solid ${COLORS.tertiary}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage('dashboard')}
              className="px-4 py-3 flex items-center gap-2 border-b-2 transition-colors"
              style={{
                borderBottomColor: currentPage === 'dashboard' ? COLORS.quaternary : 'transparent',
                color: currentPage === 'dashboard' ? COLORS.quaternary : COLORS.secondary,
                opacity: currentPage === 'dashboard' ? 1 : 0.7,
                fontWeight: currentPage === 'dashboard' ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 'dashboard') {
                  e.target.style.opacity = '1';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'dashboard') {
                  e.target.style.opacity = '0.7';
                }
              }}
            >
              <Home className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setCurrentPage('barbearias')}
              className="px-4 py-3 flex items-center gap-2 border-b-2 transition-colors"
              style={{
                borderBottomColor: currentPage === 'barbearias' ? COLORS.quaternary : 'transparent',
                color: currentPage === 'barbearias' ? COLORS.quaternary : COLORS.secondary,
                opacity: currentPage === 'barbearias' ? 1 : 0.7,
                fontWeight: currentPage === 'barbearias' ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 'barbearias') {
                  e.target.style.opacity = '1';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'barbearias') {
                  e.target.style.opacity = '0.7';
                }
              }}
            >
              <Scissors className="w-4 h-4" />
              Barbearias
            </button>
            <button
              onClick={() => setCurrentPage('multi-lojas')}
              className="px-4 py-3 flex items-center gap-2 border-b-2 transition-colors"
              style={{
                borderBottomColor: currentPage === 'multi-lojas' ? COLORS.quaternary : 'transparent',
                color: currentPage === 'multi-lojas' ? COLORS.quaternary : COLORS.secondary,
                opacity: currentPage === 'multi-lojas' ? 1 : 0.7,
                fontWeight: currentPage === 'multi-lojas' ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 'multi-lojas') {
                  e.target.style.opacity = '1';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'multi-lojas') {
                  e.target.style.opacity = '0.7';
                }
              }}
            >
              <Store className="w-4 h-4" />
              Multi-Lojas
            </button>
            <button
              onClick={() => setCurrentPage('configuracoes')}
              className="px-4 py-3 flex items-center gap-2 border-b-2 transition-colors"
              style={{
                borderBottomColor: currentPage === 'configuracoes' ? COLORS.quaternary : 'transparent',
                color: currentPage === 'configuracoes' ? COLORS.quaternary : COLORS.secondary,
                opacity: currentPage === 'configuracoes' ? 1 : 0.7,
                fontWeight: currentPage === 'configuracoes' ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 'configuracoes') {
                  e.target.style.opacity = '1';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'configuracoes') {
                  e.target.style.opacity = '0.7';
                }
              }}
            >
              <Settings className="w-4 h-4" />
              Configurações
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {currentPage === 'dashboard' && renderDashboard()}
        {currentPage === 'barbearias' && renderBarbearias()}
        {currentPage === 'multi-lojas' && renderMultiLojas()}
        {currentPage === 'configuracoes' && renderConfiguracoes()}
      </main>

      {/* Modal de Detalhes da Barbearia */}
      {showBarbeariaDetails && barbearias.find(b => b.id === showBarbeariaDetails) && (
        <BarbeariaDetailsModal
          barbearia={barbearias.find(b => b.id === showBarbeariaDetails)}
          usuarios={barbeariaUsers[showBarbeariaDetails] || []}
          efiConfig={efiConfigs[showBarbeariaDetails]}
          filiais={filiaisPorMatriz[showBarbeariaDetails] || []}
          onClose={() => setShowBarbeariaDetails(null)}
          onEfiConfigChange={(config) => setEfiConfig(config)}
          onSaveEfiConfig={() => handleSalvarEfiConfig(showBarbeariaDetails)}
          onUploadCertificate={(file) => handleUploadCertificate(showBarbeariaDetails, file)}
          onUploadKioskVideo={(id, file) => handleUploadKioskVideo(id, file)}
          onDeleteKioskVideo={(id) => handleDeleteKioskVideo(id)}
          onReloadBarbearia={carregarBarbearias}
          onAdminSubmit={(e) => handleSubmitAdmin(e, showBarbeariaDetails)}
          adminData={adminData}
          onAdminDataChange={setAdminData}
          onShowAdminForm={() => setShowAdminForm(showAdminForm === showBarbeariaDetails ? null : showBarbeariaDetails)}
          showAdminForm={showAdminForm === showBarbeariaDetails}
          onToggleBarbearia={() => handleToggleBarbearia(showBarbeariaDetails)}
          onLoadFiliais={() => carregarFiliaisMatriz(showBarbeariaDetails)}
        />
      )}

      {/* Modal Editar Trial/Plano */}
      {editingTrial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div style={{ backgroundColor: COLORS.primary }} className="rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold" style={{ color: COLORS.secondary }}>Editar Trial / Plano</h3>
                <p className="text-sm mt-1" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                  {editingTrial.nome} — ID {editingTrial.id}
                </p>
              </div>
              <button
                onClick={() => !savingTrial && setEditingTrial(null)}
                style={{ color: COLORS.secondary, opacity: 0.7 }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={trialForm.trialUnlimited}
                  onChange={(e) =>
                    setTrialForm((prev) => ({ ...prev, trialUnlimited: e.target.checked }))
                  }
                  className="w-4 h-4"
                />
                Trial / acesso ilimitado (sem cobrança automática)
              </label>
              <div>
                <label className="block text-sm font-medium mb-1">Término do trial</label>
                <input
                  type="date"
                  value={trialForm.trialEndsAt}
                  disabled={trialForm.trialUnlimited}
                  onChange={(e) =>
                    setTrialForm((prev) => ({ ...prev, trialEndsAt: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2 text-sm disabled:bg-slate-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status da assinatura</label>
                <select
                  value={trialForm.subscriptionStatus}
                  onChange={(e) =>
                    setTrialForm((prev) => ({ ...prev, subscriptionStatus: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => !savingTrial && setEditingTrial(null)}
                className="px-4 py-2 text-sm rounded border border-slate-200 hover:bg-slate-100"
                disabled={savingTrial}
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvarTrial}
                className="px-4 py-2 text-sm rounded text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: COLORS.quaternary }}
                onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#2d33a0')}
                onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = COLORS.quaternary)}
                disabled={savingTrial}
              >
                {savingTrial ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente Modal de Detalhes da Barbearia
function BarbeariaDetailsModal({
  barbearia,
  usuarios,
  efiConfig,
  filiais = [],
  onClose,
  onEfiConfigChange,
  onSaveEfiConfig,
  onUploadCertificate,
  onUploadKioskVideo,
  onDeleteKioskVideo,
  onReloadBarbearia,
  onAdminSubmit,
  adminData,
  onAdminDataChange,
  onShowAdminForm,
  showAdminForm,
  onToggleBarbearia,
  onLoadFiliais
}) {
  const [activeTab, setActiveTab] = useState('dados'); // dados, efi, admin, usuarios, filiais, assinatura

  useEffect(() => {
    if (onLoadFiliais) {
      onLoadFiliais();
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div style={{ backgroundColor: COLORS.primary }} className="rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex justify-between items-center" style={{ borderBottomColor: COLORS.tertiary }}>
          <div>
            <h3 className="text-xl font-bold" style={{ color: COLORS.secondary }}>Detalhes da Barbearia</h3>
            <p className="text-sm mt-1" style={{ color: COLORS.secondary, opacity: 0.7 }}>{barbearia.nome}</p>
          </div>
          <button 
            onClick={onClose} 
            style={{ color: COLORS.secondary, opacity: 0.7 }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        <div className="flex border-b" style={{ borderBottomColor: COLORS.tertiary }}>
          <button
            onClick={() => setActiveTab('dados')}
            className="px-4 py-3 border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === 'dados' ? COLORS.quaternary : 'transparent',
              color: activeTab === 'dados' ? COLORS.quaternary : COLORS.secondary,
              opacity: activeTab === 'dados' ? 1 : 0.7
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'dados') {
                e.target.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'dados') {
                e.target.style.opacity = '0.7';
              }
            }}
          >
            Dados
          </button>
          <button
            onClick={() => setActiveTab('efi')}
            className="px-4 py-3 border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === 'efi' ? COLORS.quaternary : 'transparent',
              color: activeTab === 'efi' ? COLORS.quaternary : COLORS.secondary,
              opacity: activeTab === 'efi' ? 1 : 0.7
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'efi') {
                e.target.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'efi') {
                e.target.style.opacity = '0.7';
              }
            }}
          >
            Certificado EFI
          </button>
          <button
            onClick={() => setActiveTab('admin')}
            className="px-4 py-3 border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === 'admin' ? COLORS.quaternary : 'transparent',
              color: activeTab === 'admin' ? COLORS.quaternary : COLORS.secondary,
              opacity: activeTab === 'admin' ? 1 : 0.7
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'admin') {
                e.target.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'admin') {
                e.target.style.opacity = '0.7';
              }
            }}
          >
            Criar Admin
          </button>
          <button
            onClick={() => setActiveTab('usuarios')}
            className="px-4 py-3 border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === 'usuarios' ? COLORS.quaternary : 'transparent',
              color: activeTab === 'usuarios' ? COLORS.quaternary : COLORS.secondary,
              opacity: activeTab === 'usuarios' ? 1 : 0.7
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'usuarios') {
                e.target.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'usuarios') {
                e.target.style.opacity = '0.7';
              }
            }}
          >
            Usuários ({usuarios.length})
          </button>
          <button
            onClick={() => setActiveTab('filiais')}
            className="px-4 py-3 border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === 'filiais' ? COLORS.quaternary : 'transparent',
              color: activeTab === 'filiais' ? COLORS.quaternary : COLORS.secondary,
              opacity: activeTab === 'filiais' ? 1 : 0.7
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'filiais') {
                e.target.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'filiais') {
                e.target.style.opacity = '0.7';
              }
            }}
          >
            <Building2 className="w-4 h-4 inline mr-1" />
            Filiais ({filiais.length})
          </button>
          <button
            onClick={() => setActiveTab('assinatura')}
            className="px-4 py-3 border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === 'assinatura' ? COLORS.quaternary : 'transparent',
              color: activeTab === 'assinatura' ? COLORS.quaternary : COLORS.secondary,
              opacity: activeTab === 'assinatura' ? 1 : 0.7
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'assinatura') {
                e.target.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'assinatura') {
                e.target.style.opacity = '0.7';
              }
            }}
          >
            <CreditCard className="w-4 h-4 inline mr-1" />
            Assinatura
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'dados' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome</label>
                <input
                  type="text"
                  value={barbearia.nome}
                  className="w-full border rounded px-3 py-2"
                  readOnly
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">CNPJ</label>
                  <input
                    type="text"
                    value={barbearia.cnpj || ''}
                    className="w-full border rounded px-3 py-2"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Telefone</label>
                  <input
                    type="text"
                    value={barbearia.telefone || ''}
                    className="w-full border rounded px-3 py-2"
                    readOnly
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Endereço</label>
                <input
                  type="text"
                  value={barbearia.endereco || ''}
                  className="w-full border rounded px-3 py-2"
                  readOnly
                />
              </div>
              
              {/* Link do Kiosk */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: COLORS.tertiary, borderColor: COLORS.tertiary }}>
                <div className="flex items-center gap-2 mb-3">
                  <QrCode className="w-5 h-5" style={{ color: COLORS.quaternary }} />
                  <label className="block text-sm font-semibold" style={{ color: COLORS.secondary }}>Link do Totem (Kiosk)</label>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    value={`${window.location.origin}/kiosk?branchId=${barbearia.id}`}
                    readOnly
                    className="flex-1 border rounded px-3 py-2 text-sm"
                    style={{ backgroundColor: COLORS.primary }}
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/kiosk?branchId=${barbearia.id}`);
                      alert('Link copiado para a área de transferência!');
                    }}
                    className="px-4 py-2 rounded text-white text-sm flex items-center gap-2 transition-colors"
                    style={{ backgroundColor: COLORS.quaternary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                    title="Copiar link"
                  >
                    <Copy className="w-4 h-4" />
                    Copiar
                  </button>
                  <a
                    href={`${window.location.origin}/kiosk?branchId=${barbearia.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded text-white text-sm flex items-center gap-2 transition-colors"
                    style={{ backgroundColor: COLORS.quaternary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                    title="Abrir link em nova aba"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Abrir
                  </a>
                </div>
                
                {/* Upload de Vídeo do Kiosk */}
                <div className="mt-6 p-4 rounded-lg border-2" style={{ backgroundColor: COLORS.tertiary, borderColor: COLORS.quaternary }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className="w-5 h-5" style={{ color: COLORS.quaternary }} />
                    <label className="block text-base font-bold" style={{ color: COLORS.secondary }}>Vídeo do Totem (Kiosk)</label>
                  </div>
                  <p className="text-sm mb-4" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                    Faça upload do vídeo que aparece na tela inicial do kiosk
                    <br />
                    <span className="text-xs">Formato: MP4 | Resolução recomendada: 1080x1920 | Tamanho máximo: 100MB</span>
                  </p>
                  {barbearia.kiosk_video_url && (
                    <div className="mb-4 p-3 rounded" style={{ backgroundColor: COLORS.primary }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: COLORS.secondary }}>Vídeo atual:</p>
                      <video 
                        src={barbearia.kiosk_video_url} 
                        className="w-full rounded border" 
                        controls 
                        muted 
                        playsInline
                        style={{ maxHeight: '200px', backgroundColor: COLORS.secondary }}
                      />
                      <p className="text-xs mt-2 break-all" style={{ color: COLORS.secondary, opacity: 0.6 }}>
                        {barbearia.kiosk_video_url}
                      </p>
                    </div>
                  )}
                  <div className="space-y-3">
                    <label className="block">
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="video/mp4"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            // Validação de tamanho (100MB)
                            if (file.size > 100 * 1024 * 1024) {
                              alert('O arquivo é muito grande. Tamanho máximo: 100MB');
                              e.target.value = '';
                              return;
                            }
                            
                            try {
                              await onUploadKioskVideo?.(barbearia.id, file);
                              alert('Vídeo enviado com sucesso!');
                              if (onReloadBarbearia) {
                                onReloadBarbearia();
                              }
                            } catch (err) {
                              alert(err?.response?.data?.message || 'Erro no upload do vídeo');
                            } finally {
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                          id={`kiosk-video-upload-${barbearia.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById(`kiosk-video-upload-${barbearia.id}`).click()}
                          className="flex-1 px-4 py-3 rounded text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                          style={{ backgroundColor: COLORS.quaternary }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                        >
                          <Settings className="w-4 h-4" />
                          {barbearia.kiosk_video_url ? 'Substituir Vídeo' : 'Fazer Upload do Vídeo'}
                        </button>
                        {barbearia.kiosk_video_url && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Tem certeza que deseja remover o vídeo do kiosk?')) {
                                try {
                                  await onDeleteKioskVideo?.(barbearia.id);
                                  alert('Vídeo removido com sucesso!');
                                  if (onReloadBarbearia) {
                                    onReloadBarbearia();
                                  }
                                } catch (err) {
                                  alert(err?.response?.data?.message || 'Erro ao remover vídeo');
                                }
                              }
                            }}
                            className="px-4 py-3 rounded text-white text-sm font-semibold transition-colors"
                            style={{ backgroundColor: COLORS.quinary }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#d13e0e'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quinary}
                          >
                            Remover Vídeo
                          </button>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <button
                  onClick={onToggleBarbearia}
                  className="px-4 py-2 rounded text-sm text-white transition-colors"
                  style={{ 
                    backgroundColor: barbearia.ativo ? COLORS.quinary : COLORS.quaternary 
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = barbearia.ativo ? '#d13e0e' : '#2d33a0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = barbearia.ativo ? COLORS.quinary : COLORS.quaternary}
                >
                  {barbearia.ativo ? 'Desativar' : 'Ativar'} Barbearia
                </button>
              </div>
            </div>
          )}

          {activeTab === 'efi' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Certificado .p12</label>
                <input
                  type="file"
                  accept=".p12,application/x-pkcs12"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUploadCertificate(file);
                    e.target.value = '';
                  }}
                  className="w-full border rounded px-3 py-2"
                />
                {efiConfig?.hasCertificate && (
                  <p className="text-xs mt-1" style={{ color: COLORS.quaternary }}>✓ Certificado configurado</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client ID</label>
                <input
                  type="text"
                  value={efiConfig?.clientId || ''}
                  onChange={(e) => onEfiConfigChange({ ...efiConfig, clientId: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client Secret</label>
                <input
                  type="password"
                  value={efiConfig?.clientSecret || ''}
                  onChange={(e) => onEfiConfigChange({ ...efiConfig, clientSecret: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Chave Pix</label>
                <input
                  type="text"
                  value={efiConfig?.pixKey || ''}
                  onChange={(e) => onEfiConfigChange({ ...efiConfig, pixKey: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <button
                onClick={onSaveEfiConfig}
                className="w-full text-white px-4 py-2 rounded transition-colors"
                style={{ backgroundColor: COLORS.quaternary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
              >
                Salvar Configurações EFI
              </button>
            </div>
          )}

          {activeTab === 'admin' && (
            <div className="space-y-4">
              <form onSubmit={onAdminSubmit}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nome *</label>
                    <input
                      type="text"
                      value={adminData.nome}
                      onChange={(e) => onAdminDataChange({ ...adminData, nome: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input
                      type="email"
                      value={adminData.email}
                      onChange={(e) => onAdminDataChange({ ...adminData, email: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Senha *</label>
                    <input
                      type="password"
                      value={adminData.senha}
                      onChange={(e) => onAdminDataChange({ ...adminData, senha: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Telefone</label>
                    <input
                      type="text"
                      value={adminData.telefone}
                      onChange={(e) => onAdminDataChange({ ...adminData, telefone: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full text-white px-4 py-2 rounded transition-colors"
                    style={{ backgroundColor: COLORS.quaternary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2d33a0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.quaternary}
                  >
                    Criar Administrador
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'usuarios' && (
            <div className="space-y-2">
              {usuarios.length === 0 ? (
                <p className="text-center py-4" style={{ color: COLORS.secondary, opacity: 0.7 }}>Nenhum usuário encontrado</p>
              ) : (
                usuarios.map((usuario) => (
                  <div key={usuario.id} className="border rounded p-3" style={{ borderColor: COLORS.tertiary }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold" style={{ color: COLORS.secondary }}>{usuario.nome}</p>
                        <p className="text-sm" style={{ color: COLORS.secondary, opacity: 0.7 }}>{usuario.email}</p>
                        <p className="text-xs" style={{ color: COLORS.secondary, opacity: 0.5 }}>Tipo: {usuario.tipo}</p>
                      </div>
                      <span
                        className="px-2 py-1 rounded text-xs"
                        style={{
                          backgroundColor: usuario.ativo ? `${COLORS.quaternary}20` : `${COLORS.quinary}20`,
                          color: usuario.ativo ? COLORS.quaternary : COLORS.quinary
                        }}
                      >
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'filiais' && (
            <div className="space-y-3">
              {filiais.length === 0 ? (
                <p className="text-center py-4" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                  Nenhuma filial cadastrada para esta matriz
                </p>
              ) : (
                filiais.map((filial) => (
                  <div key={filial.id} className="border rounded-lg p-4" style={{ borderColor: COLORS.tertiary }}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="w-5 h-5" style={{ color: COLORS.quaternary }} />
                          <h4 className="font-semibold text-lg" style={{ color: COLORS.secondary }}>
                            {filial.nome}
                          </h4>
                        </div>
                        {filial.cnpj && (
                          <p className="text-sm mb-1" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                            <strong>CNPJ:</strong> {filial.cnpj}
                          </p>
                        )}
                        {filial.telefone && (
                          <p className="text-sm mb-1" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                            <strong>Telefone:</strong> {filial.telefone}
                          </p>
                        )}
                        {filial.endereco && (
                          <p className="text-sm mb-1" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                            <strong>Endereço:</strong> {filial.endereco}
                          </p>
                        )}
                        <p className="text-xs mt-2" style={{ color: COLORS.secondary, opacity: 0.5 }}>
                          ID: {filial.id}
                        </p>
                      </div>
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: filial.ativo ? `${COLORS.quaternary}20` : `${COLORS.quinary}20`,
                          color: filial.ativo ? COLORS.quaternary : COLORS.quinary
                        }}
                      >
                        {filial.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'assinatura' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-semibold mb-2" style={{ color: COLORS.secondary }}>
                  Informações de Assinatura
                </h4>
                <p className="text-sm mb-4" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                  O gestor da barbearia pode criar a assinatura através da página de Faturamento no dashboard dele.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs" style={{ color: COLORS.secondary, opacity: 0.7 }}>
                    💡 O gestor pode escolher entre PIX ou Cartão de Crédito na página de Faturamento.
                    O checkout é feito diretamente pelo gestor.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MasterDashboard;
